<template>
  <div class="ui bottom attached tabular menu" id="flowButtonMenu">
    <router-link
      v-show="isManagedApp && codeEnabled"
      class="item"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'code' } }"
      :class="{ active: editorMode === 'code', dark: useDarkTheme }"
    >
      <i class="code icon" :title="$t('CODE')"></i>
      <span class="capitalize-fl">{{ $t('CODE') }}</span>
    </router-link>
    <router-link
      v-show="blocklyEnabled && isManagedApp && codeEnabled"
      class="item"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'blockly' } }"
      :class="{ active: editorMode === 'blockly', dark: useDarkTheme }"
    >
      <i class="cubes icon" :title="$t('BLOCKLY')"></i>
      <span class="capitalize-fl">{{ $t('BLOCKLY') }}</span>
    </router-link>
    <router-link
      class="item"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'settings' } }"
      :class="{ active: editorMode === 'settings' }"
    >
      <i class="settings icon" :title="$t('SETTINGS')"></i>
      <span class="capitalize-fl">{{ $t('SETTINGS') }}</span>
    </router-link>
    <router-link
      class="item"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'extensions' } }"
      :class="{ active: editorMode === 'extensions' }"
      v-if="flow.extensions"
    >
      <i class="puzzle piece icon" :title="$tc('EXTENSION', 2)"></i>
      <span class="capitalize-fl">{{ $tc('EXTENSION', 2) }}</span>
      <div
        class="ui circular mini label"
        :class="{ green: flowExtensionsOk, yellow: !flowExtensionsOk }"
      >
        {{ flowExtensions.length }}
      </div>
    </router-link>

    <router-link
      class="item"
      v-show="isManagedApp && codeEnabled"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'changelog' } }"
      :class="{ active: editorMode === 'changelog' }"
    >
      <i class="history icon" :title="$t('CHANGELOG')"></i>
      <span class="capitalize-fl">{{ $t('CHANGELOG') }}</span>
    </router-link>
    <router-link
      class="item"
      v-show="isManagedApp"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'debugger' } }"
      :class="{ active: editorMode === 'debugger' }"
      v-if="isDebuggerEnabled"
    >
      <i class="bug icon" :title="$t('DEBUGGER')"></i>
      <span class="capitalize-fl">{{ $t('DEBUGGER') }}</span>
      <div class="ui circular blue mini label" v-if="totalLogLines != seenLogLines">
        {{ totalLogLines - seenLogLines }}
      </div>
    </router-link>
    <router-link
      class="item"
      :to="{ name: 'app-flow', params: { appId: app.id, flowId: flow.id, mode: 'hub' } }"
      :class="{ active: editorMode === 'hub' }"
      v-if="hubEnabled"
    >
      <i class="store icon" :title="$tc('HUB', 1)"></i>
      <span class="capitalize-fl">{{ $tc('HUB', 1) }}</span>
      <!-- TODO count publications? -->
      <!--<div class="ui circular green mini label">
        2
      </div>-->
    </router-link>
    <div class="right secondary menu" style="padding-top: 8px;" v-show="editorMode !== 'changelog'">
      <debug-buttons
        v-show="
          (editorMode === 'code' || editorMode === 'blockly' || editorMode === 'debugger') &&
            isDebuggerEnabled
        "
        style="margin-right: 10px"
      />
      <loading-confirm-button
        :title="deleteHelp"
        class="ui right icon red button"
        :onClick="deleteFlow"
        :disabled="
          isCurrentFlowVersionOutdated ||
            !isCurrentUserAllowedToEditFlow ||
            isMaintainerEdited ||
            !canDelete
        "
        :confirmMessage="
          $t('DELETE_CONFIRM_MESSAGE', { thingType: $tc('FLOW', 1), thingName: flow.name })
        "
      >
        <i class="trash icon"></i>
      </loading-confirm-button>

      <loading-button
        class="ui labeled icon gray button"
        :onClick="copyFlow"
        :title="$t('COPY', { thing: '' })"
      >
        <i class="copy icon"></i>
        <span class="capitalize-fl">{{ $t('COPY', { thing: '' }) }}</span>
      </loading-button>

      <loading-button
        class="ui left labeled icon button"
        :onClick="updateFlow"
        :disabled="
          isCurrentFlowVersionOutdated ||
            (!isCurrentUserAllowedToEditFlow && !isMaintainerEdited) ||
            !isFlowEdited
        "
        :title="$t('UPDATE', { thing: '' })"
      >
        <i class="checkmark icon"></i>
        <span class="capitalize-fl">{{ $t('UPDATE', { thing: '' }) }}</span>
      </loading-button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@charset "utf-8";
@import '../../../scss/variables';
#flowButtonMenu {
  .dark.item {
    &.active {
      background-color: #1e1e1e; // From monaco dark theme
      border-color: #555555; // From Fomantic inverted segment
      color: #d4d4d4; // From monaco dark theme
    }
  }
}

@media (max-width: 900px) {
  .right.secondary.menu button {
    padding-left: 1em !important;
  }
  .right.secondary.menu {
    i {
      margin: 0 !important;
    }
    span {
      display: none;
    }
  }
}

@media (max-width: 1200px) {
  .item {
    i {
      margin: 0 !important;
    }
    span {
      display: none;
    }
  }
}
</style>
<script>
// @flow
import { LoadingButton, LoadingConfirmButton } from 'ui'
import DebugButtons from './Debugger/DebugButtons'
import { mapGetters, mapActions } from 'vuex'
import { FlowStateEnum } from 'enums'
import { mapFields, mapMultiRowFields } from 'vuex-map-fields'
import _ from 'lodash'
import { conf, Hub } from 'services'

export default {
  name: 'flow-button-menu',
  components: {
    LoadingButton,
    LoadingConfirmButton,
    DebugButtons
  },

  props: {
    deleteHelp: {
      type: String,
      default: ''
    },
    canDelete: {
      type: Boolean,
      default: () => true
    },
    deleteFlow: {
      type: Function,
      default: () => ''
    },
    updateFlow: {
      type: Function,
      default: () => ''
    },
    copyFlow: {
      type: Function,
      default: () => ''
    },
    blocklyEnabled: {
      type: Boolean,
      default: () => false
    },
    isMaintainerEdited: {
      type: Boolean,
      default: () => false
    },
    isFlowEdited: {
      type: Boolean,
      default: () => false
    },
    useDarkTheme: {
      type: Boolean,
      default: () => false
    }
  },

  data() {
    return {
      isDebuggerEnabled: !conf.disableDebugger,
      seenLogLines: 0,
      totalLogLines: 0
    }
  },

  computed: {
    ...mapGetters('flowEditor', ['isManagedApp']),

    ...mapMultiRowFields('flowEditor', {
      flowExtensions: 'flow.extensions'
    }),

    flowExtensionsOk() {
      return _.filter(this.flowExtensions, e => e.value).length == this.flowExtensions.length
    },

    isCurrentFlowVersionOutdated() {
      return this.$store.state.flowEditor.isCurrentFlowVersionOutdated
    },
    isCurrentUserAllowedToEditFlow() {
      return this.$store.state.flowEditor.isCurrentUserAllowedToEditFlow
    },
    editorMode() {
      return this.$store.state.flowEditor.editorMode
    },
    flow() {
      return this.$store.state.flowEditor.flow
    },
    debuggerLogCount() {
      return this.$store.state.debugger.logs.length
    },
    app() {
      return this.$store.state.flowEditor.app || {}
    },
    codeEnabled() {
      return !this.flow.publication || this.flow.publication.type === Hub.FLOW_PUBLICATION
    },
    hubs() {
      return this.$store.state.hub.items
    },
    hubEnabled() {
      return _.isEmpty(this.flow.publication) && !_.isEmpty(this.hubs)
    }
  },

  mounted() {
    Hub.list()
  },

  watch: {
    debuggerLogCount(newCount, oldCount) {
      if (this.editorMode != 'debugger') this.totalLogLines = newCount
    },

    editorMode(newMode) {
      if (newMode == 'debugger') this.seenLogLines = this.totalLogLines
    }
  }
}
</script>
